/**
 * Kalib AI Reveal Setup — v5.2 progressive wizard.
 *
 * Mobile-first 7-step flow + review:
 *   1. First name
 *   2. Private email                       → PATCH /api/user/update happens at Step 4
 *   3. Offer lane (company_type)
 *   4. Commercial motion (role_family)     → POST /api/intake/stage1 + PATCH /api/user/update
 *   5. Seniority                           (UI label "Manager" → backend `leader`,
 *                                            UI label "Leader"  → backend `pl_owner`)
 *   6. Capability focus                    (max 3, optional)
 *   7. Accounts                            → POST /api/intake/stage2
 *   Review → "Show my Reveal"
 *
 * Returning users:
 *   - intake_stage >= 2 → /intelligence
 *   - intake_stage == 1 (Stage 1 written, Stage 2 not) → resume at Step 5
 *   - no intake → start at Step 1
 *
 * Confidential-use philosophy: we never ask for employer, work email, job
 * title, LinkedIn, or company domain. The note at the top of the wizard is
 * intentional and must stay.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import {
  COMPANY_TYPES,
  getRoleFamilies,
} from "@/lib/intake-data";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from "lucide-react";
import { SENIORITY_OPTIONS } from "@/lib/intake-data";

// Note: email validation moved to signup page; intake no longer collects email.

function StepHeader({ step, total, title, helper, note }) {
  return (
    <div className="mb-6">
      <p
        className="uppercase tracking-widest text-amber-800 mb-2"
        style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em" }}
        data-testid="step-progress"
      >
        Step {step} of {total}
      </p>
      <h1
        className="font-heading text-zinc-950 mb-2"
        style={{ fontSize: "20px", lineHeight: 1.3, fontWeight: 700 }}
        data-testid="step-title"
      >
        {title}
      </h1>
      {helper && (
        <p className="text-zinc-700" style={{ fontSize: "14px", lineHeight: 1.5 }} data-testid="step-helper">
          {helper}
        </p>
      )}
      {note && (
        <p className="mt-2 text-zinc-500 italic" style={{ fontSize: "12px", lineHeight: 1.5 }} data-testid="step-note">
          {note}
        </p>
      )}
    </div>
  );
}

function CompactSummary({ label, value, onEdit, testId }) {
  return (
    <div
      className="flex items-center justify-between border border-zinc-200 bg-white px-3 py-2.5 mb-2"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Check className="w-3.5 h-3.5 text-emerald-700 shrink-0" strokeWidth={2.5} />
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">{label}</span>
        <span className="text-[13px] text-zinc-900 truncate">{value}</span>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-[12px] font-medium text-amber-800 hover:text-amber-900 ml-3 shrink-0"
          data-testid={`${testId}-edit`}
        >
          Edit
        </button>
      )}
    </div>
  );
}

function OptionCard({ value, title, description, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full text-left border px-4 py-3 min-h-[44px] transition-colors ${
        selected
          ? "border-amber-700 bg-amber-50/60"
          : "border-zinc-200 bg-white hover:border-zinc-400"
      }`}
      data-testid={testId}
      data-value={value}
      role="radio"
      aria-checked={selected}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 w-4 h-4 border rounded-full flex items-center justify-center shrink-0 ${
          selected ? "border-amber-700 bg-amber-700" : "border-zinc-300 bg-white"
        }`}>
          {selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
        </div>
        <div className="min-w-0">
          <p className="text-zinc-950" style={{ fontSize: "15px", fontWeight: 650, lineHeight: 1.4 }}>{title}</p>
          {description && (
            <p className="text-zinc-600 mt-0.5" style={{ fontSize: "12.5px", lineHeight: 1.4 }}>{description}</p>
          )}
        </div>
      </div>
    </button>
  );
}

export default function IntakeForm() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Per-step state (held locally; flushed to backend at Step 2 + Step 5)
  // 2026-04-27 — first_name and email are now captured at signup (Screen 1)
  // and email-verify (Screen 2). The intake wizard starts at Offer lane.
  const [step, setStep] = useState(1);
  const [companyType, setCompanyType] = useState("");
  const [roleFamily, setRoleFamily] = useState("");
  const [seniorityTier, setSeniorityTier] = useState("");
  const [followedAccounts, setFollowedAccounts] = useState([]);
  const [accountsCatalog, setAccountsCatalog] = useState([]);

  const [stage1Submitted, setStage1Submitted] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // first_name + email come from the user record. firstName is used by
  // Stage 1 write; private_email defaults to the account email.
  const firstName = (user?.first_name || "").trim();
  const privateEmail = (user?.private_email || user?.email || "").trim();

  // Initial routing — bounce or resume per spec § Returning-user routing.
  useEffect(() => {
    if (user === null) {
      navigate("/signup?next=/intake", { replace: true });
      return;
    }
    if (user === undefined) return;

    // Skip auto-redirect when the user is on the review screen — they just
    // finished Stage 2 and we want them to see the review summary.
    if (reviewMode) return;

    if (user.intake_stage >= 2) {
      navigate("/intelligence", { replace: true });
      return;
    }

    // Resume from partial intake (Stage 1 written, Stage 2 not).
    if (user.intake_stage === 1) {
      (async () => {
        try {
          const { data } = await API.get("/intake/responses");
          if (data?.company_type) setCompanyType(data.company_type);
          if (data?.role_family) setRoleFamily(data.role_family);
          if (data?.seniority_tier) setSeniorityTier(data.seniority_tier);
          if (Array.isArray(data?.followed_accounts)) setFollowedAccounts(data.followed_accounts);
          setStage1Submitted(true);
          setStep(3); // resume at Working mode (was Step 5 in old 7-step flow)
        } catch {
          /* fallback: start at Step 1 */
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, reviewMode]);

  // Load live accounts catalog (used by Step 7).
  useEffect(() => {
    API.get("/accounts")
      .then(({ data }) => setAccountsCatalog(data.accounts || []))
      .catch(() => setAccountsCatalog([]));
  }, []);

  const roleFamilies = useMemo(() => getRoleFamilies(companyType), [companyType]);
  const liveSelectedCount = useMemo(
    () => accountsCatalog.filter((a) => a.status === "live" && followedAccounts.includes(a.id)).length,
    [accountsCatalog, followedAccounts]
  );

  const toggleFollowedAccount = (id, isLive) => {
    if (!isLive) return; // coming-soon accounts cannot be selected as active
    setFollowedAccounts((prev) => {
      if (prev.includes(id)) return prev.filter((v) => v !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const canContinue = () => {
    switch (step) {
      case 1: return !!companyType;               // Offer lane
      case 2: return !!roleFamily;                // Commercial motion
      case 3: return !!seniorityTier;             // Working mode
      case 4: return liveSelectedCount > 0;       // Accounts
      default: return false;
    }
  };

  const labelForStep2 = stage1Submitted ? "Continue" : submitting ? "Saving…" : "Continue";
  const finalLabel = submitting ? "Saving…" : "Show my Reveal";

  const writeStage1 = async () => {
    setSubmitting(true);
    setError("");
    try {
      await API.post("/intake/stage1", {
        first_name: firstName,
        company_type: companyType,
        role_family: roleFamily,
      });
      // Persist private_email (defaults to account email — see top of component).
      if (privateEmail) {
        await API.patch("/user/update", { private_email: privateEmail });
      }
      setStage1Submitted(true);
      setStep(3);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const writeStage2AndReview = async () => {
    setSubmitting(true);
    setError("");
    try {
      await API.post("/intake/stage2", {
        seniority_tier: seniorityTier,
        followed_accounts: followedAccounts,
      });
      setReviewMode(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (!canContinue() || submitting) return;
    if (step === 2) {
      await writeStage1();
      return;
    }
    if (step === 4) {
      await writeStage2AndReview();
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };

  const handleBack = () => {
    setError("");
    if (step === 1) {
      // No earlier wizard step — go back to /verify-email (or /signup if
      // already verified, where Sign-in is offered). Keeps the spec
      // promise that "every screen from 2 onward has a Back button"; the
      // wizard's Step 1 is Screen 3 in the spec, so Back = Screen 2.
      navigate(user?.email_verified ? "/" : "/verify-email");
      return;
    }
    setStep((s) => Math.max(1, s - 1));
  };

  const handleShowMyReveal = () => {
    updateUser({ first_name: firstName, intake_stage: 2 });
    try { sessionStorage.removeItem("kalib_reveal_seen"); } catch { /* noop */ }
    navigate("/intelligence");
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-sm text-zinc-700">Loading…</p>
      </div>
    );
  }

  // ── Review screen ────────────────────────────────────────────
  if (reviewMode) {
    const lookup = (val, list) => list.find((o) => o.value === val);
    const companyLabel = lookup(companyType, COMPANY_TYPES)?.label || companyType;
    const roleLabel = lookup(roleFamily, roleFamilies)?.label || roleFamily;
    const seniorityLabel = lookup(seniorityTier, SENIORITY_OPTIONS)?.label || seniorityTier;
    const accountLabels = followedAccounts
      .map((id) => accountsCatalog.find((a) => a.id === id)?.name)
      .filter(Boolean)
      .join(" · ");

    // Spec Screen 8 — inline Edit per row jumps back to the relevant screen.
    // All edits return to the review screen via the standard step → review
    // flow; reviewMode flips back on when intake completes.
    const editAt = (s) => { setReviewMode(false); setStep(s); };

    return (
      <div className="min-h-screen bg-zinc-50 px-4 py-8" data-testid="intake-review">
        <div className="max-w-[480px] mx-auto">
          <div className="bg-white border border-zinc-200 p-6">
            <p className="uppercase tracking-widest text-amber-800 mb-2"
               style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em" }}>
              Review setup
            </p>
            <h1 className="font-heading text-zinc-950 mb-5"
                style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.3 }}
                data-testid="intake-review-title">
              Your Kalib AI lens
            </h1>

            <CompactSummary label="Name"              value={firstName || "—"}    testId="review-name" />
            <CompactSummary label="Email"             value={user?.email || "—"}  testId="review-email" />
            <CompactSummary label="Offer lane"        value={companyLabel}        testId="review-offer-lane"  onEdit={() => editAt(1)} />
            <CompactSummary label="Commercial motion" value={roleLabel}           testId="review-motion"      onEdit={() => editAt(2)} />
            <CompactSummary label="Working mode"      value={seniorityLabel}      testId="review-seniority"   onEdit={() => editAt(3)} />
            <CompactSummary label="Accounts"          value={accountLabels || "—"} testId="review-accounts"   onEdit={() => editAt(4)} />

            <p
              className="mt-5 text-zinc-700 italic"
              style={{ fontSize: "12.5px", lineHeight: 1.55 }}
              data-testid="review-helper-copy"
            >
              These choices shape your starting view. You can change them later to see the same accounts through a different commercial lens.
            </p>

            <Button
              onClick={handleShowMyReveal}
              className="w-full rounded-none bg-zinc-950 hover:bg-zinc-800 text-white mt-4 h-11"
              data-testid="review-show-reveal-btn"
            >
              Show my Reveal
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
            </Button>
            <button
              type="button"
              onClick={() => editAt(4)}
              className="block w-full text-center mt-3 text-[13px] text-zinc-600 hover:text-zinc-900"
              data-testid="review-back-link"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Wizard screen ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="intake-page">
      <div className="max-w-[480px] mx-auto px-4 pt-8">
        <div className="bg-white border border-zinc-200 p-6 mb-4">
          {/* STEP 1 — Offer lane (was Step 3 in old 7-step flow) */}
          {step === 1 && (
            <>
              <StepHeader
                step={1} total={4}
                title="What kind of offer do you lead with?"
                helper="Pick the one that best reflects your usual account conversations. You can change it later."
              />
              <div className="space-y-2" role="radiogroup" aria-label="Offer lane">
                {COMPANY_TYPES.map((opt) => (
                  <OptionCard
                    key={opt.value}
                    value={opt.value}
                    title={opt.label}
                    description={opt.description}
                    selected={companyType === opt.value}
                    onClick={() => { setCompanyType(opt.value); setRoleFamily(""); }}
                    testId={`intake-offer-${opt.value}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* STEP 2 — Commercial motion (was Step 4) */}
          {step === 2 && (
            <>
              <StepHeader
                step={2} total={4}
                title="What are you mostly trying to do?"
                helper="Pick the one commercial motion you most want Kalib AI to sharpen."
              />
              <div className="space-y-2" role="radiogroup" aria-label="Commercial motion">
                {roleFamilies.map((opt) => (
                  <OptionCard
                    key={opt.value}
                    value={opt.value}
                    title={opt.label}
                    description={opt.description}
                    selected={roleFamily === opt.value}
                    onClick={() => setRoleFamily(opt.value)}
                    testId={`intake-motion-${opt.value}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* STEP 3 — Working mode (was Step 5 / Seniority).
              Renamed per spec: "How do you usually operate in the account?" */}
          {step === 3 && (
            <>
              <StepHeader
                step={3} total={4}
                title="How do you usually operate in the account?"
                helper="Pick the level closest to how you work day-to-day."
              />
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Working mode">
                {SENIORITY_OPTIONS.map((opt) => {
                  const selected = seniorityTier === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSeniorityTier(opt.value)}
                      className={`px-3 py-3 border text-center transition-colors min-h-[44px] ${
                        selected ? "border-amber-700 bg-amber-50/60" : "border-zinc-200 bg-white hover:border-zinc-400"
                      }`}
                      data-testid={`intake-seniority-${opt.value}`}
                      role="radio"
                      aria-checked={selected}
                    >
                      <p className="text-zinc-950" style={{ fontSize: "14px", fontWeight: 650 }}>{opt.label}</p>
                    </button>
                  );
                })}
              </div>
              {seniorityTier && (
                <p
                  className="mt-3 text-zinc-700"
                  style={{ fontSize: "12.5px", lineHeight: 1.5 }}
                  data-testid="intake-seniority-description"
                >
                  {SENIORITY_OPTIONS.find((o) => o.value === seniorityTier)?.desc}
                </p>
              )}
            </>
          )}

          {/* STEP 4 — Accounts (was Step 7 → Step 5; Experience step retired).
              Live first; coming-soon disabled with tag. Counter on top. */}
          {step === 4 && (
            <>
              <StepHeader
                step={4} total={4}
                title="Which accounts do you want to follow first?"
                helper="Pick up to 3."
              />
              <p
                className="mb-3 text-zinc-700"
                style={{ fontSize: "12px", fontWeight: 600 }}
                data-testid="intake-accounts-counter"
              >
                {followedAccounts.length} of 3 selected
                {followedAccounts.length === 3 && (
                  <span className="ml-2 text-amber-800 font-normal italic">Limit reached</span>
                )}
              </p>
              <div className="space-y-2">
                {[...accountsCatalog]
                  .sort((a, b) => {
                    const av = a.status === "live" ? 0 : 1;
                    const bv = b.status === "live" ? 0 : 1;
                    return av - bv || (a.order || 0) - (b.order || 0);
                  })
                  .map((acc) => {
                    const isLive = acc.status === "live";
                    const selected = followedAccounts.includes(acc.id);
                    const disabled = !isLive || (!selected && followedAccounts.length >= 3 && isLive);
                    return (
                      <button
                        key={acc.id}
                        type="button"
                        onClick={() => toggleFollowedAccount(acc.id, isLive)}
                        disabled={!isLive}
                        className={`w-full text-left flex items-center justify-between border px-3 py-2.5 min-h-[44px] transition-colors ${
                          selected
                            ? "border-amber-700 bg-amber-50/60"
                            : !isLive
                            ? "border-zinc-200 bg-zinc-50 cursor-not-allowed"
                            : disabled
                            ? "border-zinc-200 bg-zinc-50 cursor-not-allowed"
                            : "border-zinc-200 bg-white hover:border-zinc-400"
                        }`}
                        data-testid={`intake-account-${acc.id}`}
                        aria-pressed={selected}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-4 h-4 border rounded flex items-center justify-center shrink-0 ${
                            selected ? "border-amber-700 bg-amber-700" : "border-zinc-300 bg-white"
                          }`}>
                            {selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                          </div>
                          <span className="text-zinc-950 truncate" style={{ fontSize: "14px", fontWeight: 600 }}>
                            {acc.name}
                          </span>
                        </div>
                        <span
                          className={`text-[10px] uppercase tracking-widest font-medium shrink-0 ${
                            isLive ? "text-emerald-700" : "text-zinc-500"
                          }`}
                        >
                          {isLive ? "Available now" : "Coming soon"}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </>
          )}

          {error && (
            <p className="mt-3 text-[12px] text-red-600" data-testid="intake-error">{error}</p>
          )}
        </div>

        {/* Sticky bottom row — Back · Continue. Spec rule: "every screen
            from 2 onward has a Back button." Step 1 still shows Back so the
            user can return to /verify-email if they want to swap the email
            on the account they just created. */}
        <div className="sticky bottom-4 mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-4 h-11 border border-zinc-300 bg-white text-sm text-zinc-700 hover:text-zinc-950 hover:border-zinc-400 transition-colors"
            data-testid="intake-back-btn"
          >
            ← Back
          </button>
          <Button
            onClick={handleContinue}
            disabled={!canContinue() || submitting}
            className="flex-1 rounded-none bg-zinc-950 hover:bg-zinc-800 text-white h-11 disabled:opacity-50"
            data-testid="intake-continue-btn"
          >
            {step === 4 ? finalLabel : labelForStep2}
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </div>
  );
}
